import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import prairiLogo from '../assets/prairiwhite.png';
import prairiLockupGrey from '../assets/prairi-lockup-grey.png';
import dashboardIconActive from '../assets/dashboard icon (off white).png';
import dashboardIconInactive from '../assets/dashboard icon (grey).png';
import favoriteHeart from '../assets/favorites_icon__red_heart__right_red.png';
import favoriteHeartGrey from '../assets/favorites_icon_grey heart2.png';
import closedMailIcon from '../assets/closedMail.png';
import openMailIcon from '../assets/openMail.png';
import settingsIconEmpty from '../assets/settings 1a.png';
import settingsIconSelected from '../assets/settings_selected.png';
import activityIconSelected from '../assets/activity_icon__selected_.png';
import activityIconNotSelected from '../assets/activity_icon__not_selected_.png';
import profileIconActive from '../assets/profilewhite.png';
import profileIconInactive from '../assets/profile_icon__grey-correct_.png';
import { useHeader } from '../contexts/HeaderContext';
import { useAuth } from '../contexts/AuthContext';
import { useFavoritesCount } from '../contexts/FavoritesCountContext';
import { useInboxUnread } from '../contexts/InboxUnreadContext';
import { MARKETING_HOME_SEARCH } from '../utils/loggedInInvestorHome';
import { forgetOpenStartupProfile } from '../utils/openStartupProfile';
import { headerHintedUserType, shouldFetchInvestorFavorites } from '../utils/signupSessionGuards';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobileMenuOpen, toggleMobileMenu, closeMobileMenu, isLoggedIn, sessionUserType } =
    useHeader();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { favoritesCount, setFavoritesCount } = useFavoritesCount();
  const { inboxUnreadCount } = useInboxUnread();
  const [showPricingDropdown, setShowPricingDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollYRef = useRef(0);
  const pricingRef = useRef<HTMLDivElement>(null);
  const functions = getFunctions();
  const mobilePricingRef = useRef<HTMLDivElement>(null);
  const pricingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const hideHeader =
    location.pathname === '/investors' ||
    location.pathname === '/startups' ||
    location.pathname === '/landing/investor';

  const isLandingPage =
    location.pathname === '/' ||
    location.pathname === '/landing/startup' ||
    location.pathname === '/startups' ||
    location.pathname === '/getfunded' ||
    location.pathname.startsWith('/getfunded/');
  const isDashboard =
    location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');
  const isFavoritesPage =
    location.pathname === '/favorites' || location.pathname.startsWith('/favorites/');
  const isInboxPage = location.pathname === '/inbox';
  const hintedType = headerHintedUserType(!!user, authLoading, sessionUserType);
  const isInvestor = user?.userType === 'investor' || hintedType === 'investor';
  const isStartup = user?.userType === 'startup' || hintedType === 'startup';
  const isProfilePage = location.pathname === '/profile/startup/view';
  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/password-reset' ||
    location.pathname.startsWith('/signup/');
  /** Shared startup teaser — nav goes to investor pricing instead of the app. */
  const isSharedStartupPage = location.pathname.startsWith('/shared/');
  const useFixedHeader = isLandingPage || isAuthPage;
  const showSolidHeader = !isLandingPage || isScrolled;

  // Close dropdown on outside click (old prairi-web behavior)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const isOutsideDesktop = pricingRef.current && !pricingRef.current.contains(e.target as Node);
      const isOutsideMobile =
        mobilePricingRef.current && !mobilePricingRef.current.contains(e.target as Node);

      if (isOutsideDesktop && isOutsideMobile) {
        setShowPricingDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Solid landing header after a short scroll. Hide on scroll down, show on scroll up.
  useEffect(() => {
    lastScrollYRef.current = window.scrollY;
    setHeaderHidden(false);
    setIsScrolled(window.scrollY > 16);

    const updateScrollState = () => {
      const y = Math.max(0, window.scrollY);
      setIsScrolled(y > 16);

      if (isMobileMenuOpen || y < 16) {
        lastScrollYRef.current = y;
        setHeaderHidden(false);
        return;
      }

      const delta = y - lastScrollYRef.current;
      lastScrollYRef.current = y;
      if (Math.abs(delta) < 4) return;
      setHeaderHidden(delta > 0);
    };

    updateScrollState();
    window.addEventListener('scroll', updateScrollState, { passive: true });
    return () => window.removeEventListener('scroll', updateScrollState);
  }, [location.pathname, isMobileMenuOpen]);

  // Fetch favorites count for badge whenever logged in as investor
  useEffect(() => {
    if (!shouldFetchInvestorFavorites(user?.userType)) {
      if (user) setFavoritesCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const getFavorites = httpsCallable(functions, 'getFavorites');
        const result = await getFavorites({});
        const data = result.data as { success?: boolean; favorites?: unknown[] };
        if (!cancelled && data?.success && Array.isArray(data.favorites)) {
          setFavoritesCount(data.favorites.length);
        } else if (!cancelled) {
          setFavoritesCount(0);
        }
      } catch {
        if (!cancelled) setFavoritesCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, functions, setFavoritesCount]);
  // Close menus automatically whenever a navigation route change finishes!
  useEffect(() => {
    closeMobileMenu();
    setShowPricingDropdown(false);
  }, [location.pathname, closeMobileMenu]);

  const handlePricingMouseEnter = () => {
    if (pricingTimeoutRef.current) clearTimeout(pricingTimeoutRef.current);
    setShowPricingDropdown(true);
  };

  const handlePricingMouseLeave = () => {
    pricingTimeoutRef.current = setTimeout(() => {
      setShowPricingDropdown(false);
    }, 300);
  };

  const goToInvestorPricing = () => {
    navigate('/investors/pricing');
    closeMobileMenu();
  };

  const handleDashboardClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    // Always open the catalog — never restore a previously viewed profile.
    forgetOpenStartupProfile();
    navigate('/dashboard', { state: { resetDashboard: true } });
    closeMobileMenu();
  };

  const handleSettingsClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    navigate('/settings', {
      state: isInvestor ? { panel: 'activity' } : undefined,
    });
    closeMobileMenu();
  };

  const handleProfileClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    navigate('/profile/startup/view');
    closeMobileMenu();
  };

  const handleFavoritesClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    navigate('/favorites');
    closeMobileMenu();
  };
  const handleInboxClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    navigate('/inbox');
    closeMobileMenu();
  };

  // On Favorites: red heart, white oval, black number. Elsewhere: grey heart, red disc, white number.
  const favoritesBadgeClass = isFavoritesPage
    ? 'border border-black bg-white text-[#111111]'
    : 'bg-red-500 text-white';
  const messagesBadgeClass =
    !isInboxPage && inboxUnreadCount > 0 ? 'bg-red-500 text-white' : 'bg-[#555] text-[#bbb]';
  const showMessagesBadge = isInboxPage || inboxUnreadCount > 0;

  const handleHomeClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    navigate({ pathname: '/', search: MARKETING_HOME_SEARCH });
    closeMobileMenu();
  };
  const handleLoginClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    navigate('/login');
    closeMobileMenu();
  };
  const handleStartupsClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    navigate('/startups');
    closeMobileMenu();
  };
  const handleInvestorsClick = () => {
    if (isSharedStartupPage) {
      goToInvestorPricing();
      return;
    }
    navigate('/investors');
    closeMobileMenu();
  };

  if (hideHeader) {
    return null;
  }

  // Old prairi-web: bg-black, shadow-lg shadow-black/60, lg:px-20, py-2 px-4, font-bold text-sm md:text-base, mr-2 md:mr-9
  return (
    <header
      className={`${useFixedHeader ? 'fixed' : 'sticky'} top-0 left-0 right-0 w-full z-[99999] transform-gpu transition-transform duration-300 ${
        headerHidden ? '-translate-y-full' : 'translate-y-0'
      } ${
        showSolidHeader
          ? 'bg-black shadow-lg shadow-black/60 text-[#f2f2f2]'
          : 'bg-transparent border-b border-transparent shadow-none text-[#f2f2f2]'
      }`}
    >
      <nav className="w-full flex justify-between items-center py-2 px-4 lg:px-20">
        {/* Logo (old: h-8 md:h-14, mr-3) */}
        <div
          className="flex items-center cursor-pointer"
          onClick={handleHomeClick}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && handleHomeClick()}
        >
          {isLoggedIn ? (
            <img
              src={prairiLockupGrey}
              alt="PRAIRI"
              className="h-4 w-auto transition-all duration-300 lg:h-9"
              data-testid="header-logo"
            />
          ) : (
            <>
              <img
                src={prairiLogo}
                alt="PRAIRI"
                className="h-7 md:h-12 mr-3 w-auto transition-all duration-300"
                data-testid="header-logo"
              />
              <span className="text-xl md:text-2xl font-bold text-white tracking-wider">
                P R A I R I
              </span>
            </>
          )}
        </div>

        {/* Desktop nav */}
        <div className="hidden lg:flex md:flex-row items-center">
          {isLoggedIn ? (
            <>
              {isAdmin && (
                <Link
                  to={isSharedStartupPage ? '/investors/pricing' : '/admindashboard'}
                  onClick={closeMobileMenu}
                  className="font-bold text-sm md:text-base mr-2 md:mr-9 flex items-center gap-1.5 text-white"
                >
                  Admin
                </Link>
              )}
              <button
                onClick={handleDashboardClick}
                className={`${isDashboard ? 'font-bold' : 'font-normal'} text-sm md:text-base mr-2 md:mr-9 flex items-center gap-1.5 transition-colors ${
                  isDashboard ? 'text-white' : 'text-[#c8c8c8] hover:text-white'
                }`}
              >
                <img
                  src={isDashboard ? dashboardIconActive : dashboardIconInactive}
                  alt=""
                  className="w-6 h-6 flex-shrink-0 object-contain"
                  aria-hidden
                />
                My Dashboard
              </button>
              {isStartup && (
                <button
                  onClick={handleProfileClick}
                  className={`${isProfilePage ? 'font-bold' : 'font-normal'} text-sm md:text-base mr-2 md:mr-9 flex items-center gap-1.5 transition-colors ${
                    isProfilePage ? 'text-white' : 'text-[#c8c8c8] hover:text-white'
                  }`}
                >
                  <img
                    src={isProfilePage ? profileIconActive : profileIconInactive}
                    alt=""
                    className="w-6 h-6 flex-shrink-0 object-contain"
                    aria-hidden
                  />
                  My Profile
                </button>
              )}
              {isInvestor && (
                <button
                  onClick={handleFavoritesClick}
                  className={`${isFavoritesPage ? 'font-bold' : 'font-normal'} text-sm md:text-base mr-2 md:mr-9 flex items-center gap-1.5 transition-colors ${
                    isFavoritesPage ? 'text-white' : 'text-[#c8c8c8] hover:text-white'
                  }`}
                >
                  <span className="relative inline-flex flex-shrink-0 pr-2.5">
                    <img
                      src={isFavoritesPage ? favoriteHeart : favoriteHeartGrey}
                      alt=""
                      className="w-6 h-6 object-contain"
                      aria-hidden
                    />
                    {favoritesCount !== null && (
                      <span
                        className={`absolute top-1.5 right-0.5 flex items-center justify-center h-3.5 min-w-[1.1rem] px-1 rounded-full text-[10px] font-semibold leading-none ${favoritesBadgeClass}`}
                      >
                        {favoritesCount}
                      </span>
                    )}
                  </span>
                  Favorites
                </button>
              )}
              <button
                onClick={handleInboxClick}
                className={`${isInboxPage ? 'font-bold' : 'font-normal'} text-sm md:text-base mr-2 md:mr-9 flex items-center gap-1.5 transition-colors ${
                  isInboxPage ? 'text-white' : 'text-[#c8c8c8] hover:text-white'
                }`}
              >
                <span className="relative inline-flex flex-shrink-0">
                  <img
                    src={isInboxPage ? openMailIcon : closedMailIcon}
                    alt=""
                    className="w-7 h-7 mb-2 flex-shrink-0 object-contain"
                    aria-hidden
                  />
                  {showMessagesBadge && (
                    <span
                      className={`absolute -top-1.5 -right-2.5 flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full text-xs font-semibold leading-none ${messagesBadgeClass}`}
                    >
                      {inboxUnreadCount}
                    </span>
                  )}
                </span>
                Messages
              </button>
              <button
                onClick={handleSettingsClick}
                className={`${
                  location.pathname === '/settings' ? 'font-bold' : 'font-normal'
                } text-sm md:text-base mr-2 md:mr-9 flex items-center gap-1.5 transition-colors ${
                  location.pathname === '/settings'
                    ? 'text-white'
                    : 'text-[#c8c8c8] hover:text-white'
                }`}
              >
                <img
                  src={
                    isInvestor
                      ? location.pathname === '/settings'
                        ? activityIconSelected
                        : activityIconNotSelected
                      : location.pathname === '/settings'
                        ? settingsIconSelected
                        : settingsIconEmpty
                  }
                  alt=""
                  className="w-6 h-6 flex-shrink-0 object-contain"
                  aria-hidden
                />
                {isInvestor ? 'Activity & Settings' : 'Settings'}
              </button>
            </>
          ) : (
            <div className="flex items-center">
              <button
                onClick={handleStartupsClick}
                className="font-bold text-sm md:text-base mr-2 md:mr-9 text-white"
              >
                Startups
              </button>
              <button
                onClick={handleInvestorsClick}
                className="font-bold text-sm md:text-base mr-2 md:mr-9 text-white"
              >
                Investors
              </button>
              <div
                ref={pricingRef}
                className="relative"
                onMouseEnter={isSharedStartupPage ? undefined : handlePricingMouseEnter}
                onMouseLeave={isSharedStartupPage ? undefined : handlePricingMouseLeave}
              >
                <button
                  className="font-bold text-sm md:text-base mr-2 md:mr-9 text-white flex items-center"
                  onClick={e => {
                    e.preventDefault();
                    if (isSharedStartupPage) {
                      goToInvestorPricing();
                      return;
                    }
                    setShowPricingDropdown(prev => !prev);
                  }}
                >
                  Pricing
                  {!isSharedStartupPage && (
                    <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
                {!isSharedStartupPage && showPricingDropdown && (
                  <div className="absolute top-full left-0 pt-2 w-48 z-50">
                    <div className="bg-black/90 border border-gray-700 rounded-md shadow-lg py-2">
                      <Link
                        to="/startups/pricing"
                        className="block w-full text-left px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-gray-800/50"
                        onClick={() => {
                          setShowPricingDropdown(false);
                          closeMobileMenu();
                        }}
                      >
                        For Startups
                      </Link>
                      <Link
                        to="/investors/pricing"
                        className="block w-full text-left px-4 py-2 text-sm text-white/80 hover:text-white hover:bg-gray-800/50"
                      >
                        For Investors
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleLoginClick}
                className="font-bold text-sm md:text-base mr-2 md:mr-9 text-white"
              >
                Log in
              </button>
            </div>
          )}
        </div>

        {/* Mobile: icons only when signed in. Guests keep the menu. */}
        <div className="lg:hidden flex items-center gap-4">
          {isLoggedIn ? (
            <>
              {isAdmin && (
                <Link
                  to={isSharedStartupPage ? '/investors/pricing' : '/admindashboard'}
                  className="text-xs font-bold text-white"
                >
                  Admin
                </Link>
              )}
              <div className="flex items-center gap-5">
                <button
                  type="button"
                  onClick={handleDashboardClick}
                  className="flex items-center"
                  aria-label="My Dashboard"
                >
                  <img
                    src={isDashboard ? dashboardIconActive : dashboardIconInactive}
                    alt=""
                    className="h-6 w-6 object-contain"
                  />
                </button>
                {isStartup && (
                  <button
                    type="button"
                    onClick={handleProfileClick}
                    className="flex items-center"
                    aria-label="My Profile"
                  >
                    <img
                      src={isProfilePage ? profileIconActive : profileIconInactive}
                      alt=""
                      className="h-6 w-6 object-contain"
                    />
                  </button>
                )}
                {isInvestor && (
                  <button
                    type="button"
                    onClick={handleFavoritesClick}
                    className="flex items-center"
                    aria-label="Favorites"
                  >
                    <span className="relative inline-flex flex-shrink-0 pr-2.5">
                      <img
                        src={isFavoritesPage ? favoriteHeart : favoriteHeartGrey}
                        alt=""
                        className="h-6 w-6 object-contain"
                      />
                      {favoritesCount !== null && (
                        <span
                          className={`absolute top-1.5 right-0.5 flex items-center justify-center h-3.5 min-w-[1.1rem] px-1 rounded-full text-[10px] font-semibold leading-none ${favoritesBadgeClass}`}
                        >
                          {favoritesCount}
                        </span>
                      )}
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleInboxClick}
                  className="relative flex items-center"
                  aria-label="Messages"
                >
                  <img
                    src={isInboxPage ? openMailIcon : closedMailIcon}
                    alt=""
                    className="h-6 w-6 object-contain"
                  />
                  {showMessagesBadge && (
                    <span
                      className={`absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none ${messagesBadgeClass}`}
                    >
                      {inboxUnreadCount}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleSettingsClick}
                  className="flex items-center"
                  aria-label={isInvestor ? 'Activity and Settings' : 'Settings'}
                >
                  <img
                    src={
                      isInvestor
                        ? location.pathname === '/settings'
                          ? activityIconSelected
                          : activityIconNotSelected
                        : location.pathname === '/settings'
                          ? settingsIconSelected
                          : settingsIconEmpty
                    }
                    alt=""
                    className="h-6 w-6 object-contain"
                  />
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={toggleMobileMenu}
              className="text-[#c8c8c8] hover:text-white"
              data-testid="mobile-menu-button"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              <svg
                className="h-6 w-6 md:h-8 md:w-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          )}
        </div>
      </nav>

      {/* Mobile menu (old: same links in a row) */}
      {isMobileMenuOpen && (
        <div className="w-full mx-auto my-4 px-4 text-xs md:text-base lg:hidden border-t border-gray-700/50">
          <div className="flex flex-wrap justify-between gap-2 pt-3">
            {isLoggedIn ? (
              <>
                <button
                  onClick={handleDashboardClick}
                  className={`${isDashboard ? 'font-bold text-white' : 'font-normal text-[#c8c8c8]'} flex items-center gap-1.5`}
                >
                  <img
                    src={isDashboard ? dashboardIconActive : dashboardIconInactive}
                    alt=""
                    className="w-6 h-6 object-contain"
                    aria-hidden
                  />
                  My Dashboard
                </button>
                {isStartup && (
                  <button
                    onClick={handleProfileClick}
                    className={`${isProfilePage ? 'font-bold text-white' : 'font-normal text-[#c8c8c8]'} flex items-center gap-1.5`}
                  >
                    <img
                      src={isProfilePage ? profileIconActive : profileIconInactive}
                      alt=""
                      className="w-6 h-6 object-contain"
                      aria-hidden
                    />
                    My Profile
                  </button>
                )}
                {isInvestor && (
                  <button
                    onClick={handleFavoritesClick}
                    className={`${isFavoritesPage ? 'font-bold text-white' : 'font-normal text-[#c8c8c8]'} flex items-center gap-1.5`}
                  >
                    <span className="relative inline-flex flex-shrink-0 pr-2.5">
                      <img
                        src={isFavoritesPage ? favoriteHeart : favoriteHeartGrey}
                        alt=""
                        className="w-6 h-6 object-contain"
                        aria-hidden
                      />
                      {favoritesCount !== null && (
                        <span
                          className={`absolute top-1.5 right-0.5 flex items-center justify-center h-3.5 min-w-[1.1rem] px-1 rounded-full text-[10px] font-semibold leading-none ${favoritesBadgeClass}`}
                        >
                          {favoritesCount}
                        </span>
                      )}
                    </span>
                    Favorites
                  </button>
                )}
                <button
                  onClick={handleInboxClick}
                  className={`${isInboxPage ? 'font-bold text-white' : 'font-normal text-[#c8c8c8]'} flex items-center gap-1.5`}
                >
                  <span className="relative inline-flex flex-shrink-0">
                    <img
                      src={isInboxPage ? openMailIcon : closedMailIcon}
                      alt=""
                      className="w-7 h-7 object-contain"
                      aria-hidden
                    />
                    {showMessagesBadge && (
                      <span
                        className={`absolute -top-2.5 -right-2.5 flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full text-xs font-semibold leading-none ${messagesBadgeClass}`}
                      >
                        {inboxUnreadCount}
                      </span>
                    )}
                  </span>
                  Messages
                </button>
                <button
                  onClick={handleSettingsClick}
                  className={`${
                    location.pathname === '/settings'
                      ? 'font-bold text-white'
                      : 'font-normal text-[#c8c8c8]'
                  } flex items-center gap-1.5`}
                >
                  <img
                    src={
                      isInvestor
                        ? location.pathname === '/settings'
                          ? activityIconSelected
                          : activityIconNotSelected
                        : location.pathname === '/settings'
                          ? settingsIconSelected
                          : settingsIconEmpty
                    }
                    alt=""
                    className="w-6 h-6 object-contain"
                    aria-hidden
                  />
                  {isInvestor ? 'Activity & Settings' : 'Settings'}
                </button>
                {isAdmin && (
                  <Link
                    to={isSharedStartupPage ? '/investors/pricing' : '/admindashboard'}
                    onClick={closeMobileMenu}
                    className="font-bold text-white flex items-center gap-1.5"
                  >
                    Admin
                  </Link>
                )}
              </>
            ) : (
              <>
                <button onClick={handleStartupsClick} className="font-bold text-white">
                  Startups
                </button>
                <button onClick={handleInvestorsClick} className="font-bold text-white">
                  Investors
                </button>
                <div ref={mobilePricingRef} className="flex flex-col">
                  <button
                    onClick={() => {
                      if (isSharedStartupPage) {
                        goToInvestorPricing();
                        return;
                      }
                      setShowPricingDropdown(!showPricingDropdown);
                    }}
                    className="font-bold text-white text-left"
                  >
                    Pricing
                  </button>
                  {!isSharedStartupPage && showPricingDropdown && (
                    <div className="w-full flex flex-col gap-1 mt-2 pl-2 border-l border-gray-600">
                      <Link
                        to="/startups/pricing"
                        className="block w-full text-left py-1 text-sm text-white/80 hover:text-white"
                        onClick={() => {
                          setShowPricingDropdown(false);
                          closeMobileMenu();
                        }}
                      >
                        For Startups
                      </Link>
                      <Link
                        to="/investors/pricing"
                        className="block w-full text-left py-1 text-sm text-white/80 hover:text-white"
                      >
                        For Investors
                      </Link>
                    </div>
                  )}
                </div>
                <button onClick={handleLoginClick} className="font-bold text-white">
                  Log in
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
